import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPencil, LuTrash, LuPlus, LuEye } from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
} from "@/components/data-table";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatCurrency, formatDate } from "@/utils";
import { useUser } from "@/context/UserContext";

// Initial filter values
const initialFilters = {
    order_number: "",
    customer_id: "",
    payment_status_id: "",
    payment_method_id: "",
    date_from: "",
    date_to: "",
};

// Skeleton columns for loading state
const skeletonColumns = [
    { name: "No.", skeletonWidth: "w-12" },
    { name: "No. Order", skeletonWidth: "w-32" },
    { name: "Pelanggan", skeletonWidth: "w-40" },
    { name: "Tanggal", skeletonWidth: "w-28" },
    { name: "Total", skeletonWidth: "w-32", align: "end" },
    { name: "Status Pembayaran", skeletonWidth: "w-24" },
    { name: "Aksi", skeletonWidth: "w-20", align: "end" },
];

const SalesOrderData = () => {
    const { hasPermission } = useUser();
    const table = useDataTable(
        "sales-orders/data",
        "sales-orders/{id}/delete",
        {},
        initialFilters,
    );

    // Fetch filter options for label lookups
    const { data: customers } = useFetchData("select/customer");
    const { data: statuses } = useFetchData("select/payment-status");
    const { data: paymentMethods } = useFetchData("select/payment-method");

    // Filter options to pass to FilterData component (handle null with || [])
    const filterOptions = {
        customers: customers || [],
        statuses: statuses || [],
        paymentMethods: paymentMethods || [],
    };

    // Filter labels with dynamic getValueLabel
    const filterLabels = {
        order_number: { label: "No. Order" },
        customer_id: {
            label: "Pelanggan",
            getValueLabel: (val) => {
                const item = customers?.find(
                    (c) => String(c.value) === String(val),
                );
                return item?.title || val;
            },
        },
        payment_status_id: {
            label: "Status Pembayaran",
            getValueLabel: (val) => {
                const item = statuses?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        payment_method_id: {
            label: "Metode Bayar",
            getValueLabel: (val) => {
                const item = paymentMethods?.find(
                    (p) => String(p.value) === String(val),
                );
                return item?.title || val;
            },
        },
        date_from: { label: "Dari Tanggal" },
        date_to: { label: "Sampai Tanggal" },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Penjualan"
                    message={table.error?.message}
                    onRetry={table.refetch}
                />
            </div>
        );
    }

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Penjualan"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Penjualan" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("sales-order.create") && (
                        <Button
                            as={Link}
                            to="/sales-order/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Penjualan
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada penjualan yang cocok dengan filter."
                                    : "Belum ada data penjualan."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Penjualan" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="order_number">
                                    No. Order
                                </TableColumn>
                                <TableColumn key="customer">
                                    Pelanggan
                                </TableColumn>
                                <TableColumn key="date">Tanggal</TableColumn>
                                <TableColumn key="total" align="end">
                                    Total
                                </TableColumn>
                                <TableColumn key="status">
                                    Status Pembayaran
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        {/* No. */}
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {table.items.indexOf(item) +
                                                    1 +
                                                    (table.pagination.page -
                                                        1) *
                                                        table.pagination
                                                            .perPage}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        {/* Order Number */}
                                        <TableCell>
                                            <ThemedText size="sm" className="font-medium">
                                                {item.order_number}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Customer */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.customer?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Date */}
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatDate(item.order_date)}
                                            </ThemedText>
                                        </TableCell>
                                        {/* Total & Payment Method */}
                                        <TableCell>
                                            <div className="flex flex-col items-end">
                                                <ThemedText
                                                    size="sm"
                                                    className="font-medium"
                                                >
                                                    {formatCurrency(
                                                        item.total_amount,
                                                    )}
                                                </ThemedText>
                                                <ThemedText
                                                    size="xs"
                                                    variant="secondary"
                                                >
                                                    {item.payment_method
                                                        ?.name || "-"}
                                                </ThemedText>
                                            </div>
                                        </TableCell>
                                        {/* Status */}
                                        <TableCell>
                                            <Tooltip
                                                content={
                                                    item.latest_payment_date
                                                        ? `Terakhir dibayar: ${formatDate(item.latest_payment_date)}`
                                                        : "Belum ada pembayaran"
                                                }
                                                placement="top"
                                            >
                                                <Chip
                                                    color={
                                                        item.payment_status
                                                            ?.badge_color ||
                                                        "default"
                                                    }
                                                    size="sm"
                                                    className="cursor-default font-medium"
                                                    variant="flat"
                                                >
                                                    {item.payment_status?.name ||
                                                        "Unknown"}
                                                </Chip>
                                            </Tooltip>
                                        </TableCell>
                                        {/* Actions */}
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Lihat Detail">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/sales-order/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {(() => {
                                                    const isPaid = [3, 4].includes(item.payment_status_id);
                                                    const canEdit = hasPermission("sales-order.edit") && (!isPaid || hasPermission("sales-order.edit-paid"));
                                                    const canDelete = hasPermission("sales-order.delete") && (!isPaid || hasPermission("sales-order.delete-paid"));
                                                    const editLocked = hasPermission("sales-order.edit") && isPaid && !hasPermission("sales-order.edit-paid");
                                                    const deleteLocked = hasPermission("sales-order.delete") && isPaid && !hasPermission("sales-order.delete-paid");
                                                    return (
                                                        <>
                                                            {canEdit && (
                                                                <Tooltip content="Edit Order">
                                                                    <Button
                                                                        isIconOnly
                                                                        size="sm"
                                                                        variant="light"
                                                                        as={Link}
                                                                        to={`/sales-order/${item.id}/edit`}
                                                                    >
                                                                        <LuPencil size={14} className="text-default-500" />
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            {editLocked && (
                                                                <Tooltip content="Tidak dapat diedit karena sudah lunas">
                                                                    <span>
                                                                        <Button isIconOnly size="sm" variant="light" isDisabled className="opacity-30">
                                                                            <LuPencil size={14} />
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                            {canDelete && (
                                                                <Tooltip content="Hapus Order">
                                                                    <Button
                                                                        isIconOnly
                                                                        size="sm"
                                                                        color="danger"
                                                                        variant="light"
                                                                        onPress={() => table.deleteState.openDeleteModal(item.id)}
                                                                    >
                                                                        <LuTrash size={14} />
                                                                    </Button>
                                                                </Tooltip>
                                                            )}
                                                            {deleteLocked && (
                                                                <Tooltip content="Tidak dapat dihapus karena sudah lunas">
                                                                    <span>
                                                                        <Button isIconOnly size="sm" variant="light" isDisabled className="opacity-30">
                                                                            <LuTrash size={14} />
                                                                        </Button>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />
        </div>
    );
};

export default SalesOrderData;
